<script setup lang="ts">
import type { Editor } from '@tiptap/vue-3'
import type { TooltipContentProps } from 'radix-vue'
import { ref } from 'vue'

import { ActionButton, EditorColorPicker, EditorIcon } from '@/components/editor'
import { ButtonViewReturnComponentProps } from '@/types'
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui'

interface Props {
  editor: Editor
  tooltip?: string
  disabled?: boolean
  action?: ButtonViewReturnComponentProps['action']
  isActive?: ButtonViewReturnComponentProps['isActive']
  tooltipOptions?: TooltipContentProps
}

const props = withDefaults(defineProps<Props>(), {
  tooltip: undefined,
  disabled: false,
  action: undefined,
  tooltipOptions: undefined,
  isActive: undefined,
})

const selectedHeight = ref<string>('1')
const selectedColor = ref<string>('#000000')

function insertBorder() {
  props.action?.({
    height: selectedHeight.value,
    color: selectedColor.value,
  })
}

function onColorChange(color: string | undefined) {
  if (color) {
    selectedColor.value = color
  }
}
</script>

<template>
  <div class="hover:bg-muted flex h-[32px] items-center gap-1 rounded-md">
    <ActionButton :tooltip="tooltip" :disabled="disabled" :action="insertBorder" :tooltip-options="tooltipOptions">
      <template #icon>
        <span class="flex items-center justify-center text-sm">
          <svg width="18px" height="18px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="2" y1="12" x2="22" y2="12" :stroke="selectedColor" :stroke-width="selectedHeight" />
            <rect x="1" y="11" width="22" height="2" rx="1" stroke="currentColor" fill="none" />
          </svg>
        </span>
      </template>
    </ActionButton>
    <Select v-model="selectedHeight" :disabled="disabled">
      <SelectTrigger class="h-7 p-1 text-xs outline-0">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="1">1px</SelectItem>
        <SelectItem value="2">2px</SelectItem>
        <SelectItem value="3">3px</SelectItem>
      </SelectContent>
    </Select>

    <EditorColorPicker v-model="selectedColor" :disabled="disabled" @change="onColorChange">
      <Button
        variant="ghost"
        size="icon"
        class="hover:bg-muted-foreground/20 h-[32px] w-3 rounded-l-none"
        :disabled="disabled"
      >
        <EditorIcon class="h-3 w-3 text-zinc-500" name="MenuDown" />
      </Button>
    </EditorColorPicker>
  </div>
</template>
